import { memo, type ReactNode } from 'react';

import { WorkspaceAgentDisclosure } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/disclosure/WorkspaceAgentDisclosure';
import { type WorkspaceAgentPhaseListItem } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/types';
import { WorkspaceAgentPhaseList } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/phases/WorkspaceAgentPhaseList';

export type WorkspaceAgentLoadingDisclosureProps = {
  'data-testid'?: string;
  defaultIsExpanded?: boolean;
  /** Applied to every step, so a test can query the set. Matches every step rather than one. */
  itemTestId?: string;
  /** The live status line, e.g. `Working on it…`. */
  label: string;
  /**
   * The animated mark beside the label. Taken as a node so this stays clear of the animation library
   * and the theme hook it needs — the panel supplies `WorkspaceAgentPanelToolGroupLoader`.
   */
  mark?: ReactNode;
  phases: WorkspaceAgentPhaseListItem[];
};

/**
 * What the agent is doing right now, with the steps behind it available on demand.
 *
 * Collapsed by default because a run in progress is usually just something to wait out; the steps are
 * what you open when it is taking longer than it should.
 */
export const WorkspaceAgentLoadingDisclosure = memo(
  ({
    'data-testid': testId,
    defaultIsExpanded,
    itemTestId,
    label,
    mark,
    phases,
  }: WorkspaceAgentLoadingDisclosureProps) => (
    <>
      {/*
        The status is the one thing in the panel that changes on its own, so it is announced rather
        than left to be found. The announcement is a sibling of the disclosure instead of an
        `aria-live` on the label, because the label lives inside the trigger `button`, and a button's
        descendants are presentational — most screen readers would never expose a live region there.
        `output` carries `role="status"` on its own, which is the polite announcement this wants, and
        it is the same trick the panel header uses for its own status. `sr-only` is out of flow, so it
        cannot open a gap in whatever lays this out.
      */}
      <output className="sr-only">{label}</output>

      <WorkspaceAgentDisclosure
        data-testid={testId}
        defaultIsExpanded={defaultIsExpanded}
        trigger={
          <span className="flex items-center gap-2">
            {mark}
            <span className="min-w-0 truncate font-mono ads:text-metadata text-12 text-gray-10 leading-4">{label}</span>
          </span>
        }
        triggerTooltip={label}
        variant="plain"
      >
        {/* Indented to start under the label rather than under the mark, which pairs with the size-6
            mark the panel passes. */}
        <div className="pl-8">
          <WorkspaceAgentPhaseList itemTestId={itemTestId} phases={phases} variant="steps" />
        </div>
      </WorkspaceAgentDisclosure>
    </>
  ),
);

WorkspaceAgentLoadingDisclosure.displayName = 'WorkspaceAgentLoadingDisclosure';
